import * as React from 'react';
import { ForwardedRef, forwardRef, useMemo } from 'react';
import { DataRequestType, DataRequestVO } from '@mc/data-filter-react';
import { IData, VirtualDataRef } from '../virtual-data/VirtualData';
import { BaseTableProps } from '../table/TableProps';
import { BaseDataTableProps, DataTable } from './DataTable';
import { ClientFilter } from './ClientTable';
import { Pagination } from '../pagination/Pagination';
import { Classes } from '../index';

export type PaginationProps = {
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  totalItems: number;
};
type Props<DATA extends IData<ID>, ID, FILTER, DATA_FOOTER> = {
  /*
    Objekt v 'rows' musí obsahovat propertu 'id', nebo je třeba použít funkci 'getItemId'.
     */
  rows?: DATA[];
  clientFilter?: ClientFilter<FILTER>;
} & BaseDataTableProps<DATA, ID, any, DATA_FOOTER> &
  BaseTableProps<DATA> &
  PaginationProps;

const PaginationTableIn = <
  DATA extends IData<ID>,
  ID extends unknown = {},
  FILTER extends unknown = {},
  DATA_FOOTER extends object = {}
>(
  {
    rows,
    noVerticalScroll,
    clientFilter,
    page,
    pageSize,
    onPageChange,
    totalItems,
    setLastVisitedRowIndex,
    ...rest
  }: Props<DATA, ID, FILTER, DATA_FOOTER>,
  ref: ForwardedRef<VirtualDataRef<ID>>
) => {
  const setPage = (page: number) => {
    onPageChange(page);
    if (setLastVisitedRowIndex) {
      setLastVisitedRowIndex(0);
    }
  };

  const hasFooter = useMemo(
    () => rest.columns?.some((x) => x.footer),
    [rest.columns]
  );
  const apiRequest = async (request: DataRequestVO<any>) => {
    switch (request.requestType) {
      case DataRequestType.DATA:
        if (request.idsRequest) {
          return {
            rows: rows
              ? rows.filter((x: any) => {
                  // @ts-ignore
                  return request.idsRequest?.ids.indexOf(getItemId(x)) >= 0;
                })
              : [],
            totalElements: rows?.length || 0,
          };
        }
    }
    return {
      rows: [],
      totalElements: 0,
    };
  };

  const getItemId = rest.getItemId || ((item: DATA) => item?.id);

  const ids = useMemo<ID[] | undefined>(() => {
    if (!rows) {
      return undefined;
    }

    // @ts-ignore
    const res: ID[] = rows.map((row) => getItemId(row));
    return res;
  }, [rows]);
  return (
    <div className={Classes.PAGINATION_TABLE}>
      <DataTable<DATA, any, ID, DATA_FOOTER>
        noVerticalScroll={noVerticalScroll}
        totalElements={rows?.length}
        ids={ids}
        ref={ref}
        // @ts-ignore
        api={apiRequest}
        setLastVisitedRowIndex={setLastVisitedRowIndex}
        pageNumber={page}
        throttleTime={150}
        initialCache={rows}
        {...rest}
      />
      <Pagination
        hasFooter={hasFooter}
        page={page}
        pageSize={pageSize}
        totalItems={totalItems}
        setPage={setPage}
      />
    </div>
  );
};

export const PaginationTable = forwardRef(PaginationTableIn) as <
  DATA extends IData<ID>,
  ID extends unknown = {},
  FILTER extends unknown = {},
  DATA_FOOTER extends object = {}
>(
  props: Props<DATA, ID, FILTER, DATA_FOOTER> & {
    ref?: React.ForwardedRef<VirtualDataRef<ID>>;
  }
) => ReturnType<typeof PaginationTableIn>;
